import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { Big } from 'big.js';
import * as _ from 'lodash';
import { PledgeProductRead } from '../../definition/interface/Pledge';
import { FormConfig } from '../../definition/types/subscription';
import FormGroupArray from '../../services/FormGroupArray';

// PRE-4896 : builder dédié à la slide "Taux par support". Ne gère que les champs nécessaires à
// cette slide (isin/name/type/riskScore + les 8 champs de taux) — les montants de répartition
// (initalRepartition/monthlyRepartition) sont conservés tels quels, cette slide ne les modifie pas.
//
// Les 4 champs "*Saisi" sont affichés dans le tableau via un input brut (eaNumericInput, cf.
// isin-table), pas via pr-input-percent : le FormControl porte donc directement la valeur affichée
// en pourcentage (0-100), pas la proportion stockée en base (0-1). D'où la conversion *100 à
// l'initialisation (toPercent) et /100 à l'extraction (fromPercent), à l'identique du pattern déjà
// utilisé par isin-table pour initalRepartition/monthlyRepartition.
// Big plutôt que Math.round : évite le cas classique où l'étape intermédiaire *100000 est elle-même
// imprécise en flottant et fait arrondir dans le mauvais sens (cf. isin-table/ea-subscription-form.builder.ts).
function toPercent(value: number | null | undefined): number | null {
  return value != null ? new Big(value).times(100).round(3).toNumber() : null;
}
function fromPercent(value: number | string | null | undefined): number | null {
  return value != null && value !== '' ? +value / 100 : null;
}
@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  options: any = {};

  generateForm(entity: PledgeProductRead & { parameters: any }, options) {
    this.options = options;

    let supportRepartition: UntypedFormGroup[] = [];

    const supportRepartitionIsin = entity?.parameters?.supportRepartition;
    if (supportRepartitionIsin)
      _.forEach(supportRepartitionIsin, scpi =>
        supportRepartition.push(this.formBuilder.group(this.createModel(scpi))),
      );

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        supportRepartition: this.formBuilder.array(
          supportRepartition,
          Validators.nullValidator,
        ),
      }),
    };

    return this.formBuilder.group(formConfig, {});
  }
  generate(entity: any[], option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }
  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  get supportRepartition() {
    return this.products
      .get(`${0}`)
      .get('parameters')
      .get('supportRepartition') as UntypedFormArray;
  }
  public createModel(item): any {
    return {
      isin: [item?.isin || null],
      name: [item?.name || null],
      type: [item?.type || null],
      riskScore: [item?.riskScore ?? null],
      initalRepartition: [item?.initalRepartition ?? 0],
      monthlyRepartition: [item?.monthlyRepartition ?? 0],
      // Préremplis par la commande COMPLETE_SUPPORT_RATES (résolution base support), jamais saisis
      // directement — servent de placeholder et de valeur par défaut du champ "Saisi" ci-dessous.
      upfrontAssureurAutocomplete: [item?.upfrontAssureurAutocomplete ?? null],
      upfrontEmetteurAutocomplete: [item?.upfrontEmetteurAutocomplete ?? null],
      fraisGestionAutocomplete: [item?.fraisGestionAutocomplete ?? null],
      retrocessionAssureurAutocomplete: [
        item?.retrocessionAssureurAutocomplete ?? null,
      ],
      // Valeur effectivement retenue pour cette vente : initialisée depuis la valeur déjà saisie
      // (rejeu du formulaire) sinon depuis la valeur préremplie ci-dessus — convertie en pourcentage
      // (0-100) pour l'affichage dans l'input brut. Désactivée en lecture seule pour les
      // organisations affiliées EUODIA (cf. taux-par-support.component.ts) — un champ désactivé
      // est exclu de `form.value`, donc jamais transmis au submit.
      upfrontAssureurSaisi: [
        toPercent(item?.upfrontAssureurSaisi ?? item?.upfrontAssureurAutocomplete),
      ],
      upfrontEmetteurSaisi: [
        toPercent(item?.upfrontEmetteurSaisi ?? item?.upfrontEmetteurAutocomplete),
      ],
      fraisGestionSaisi: [
        toPercent(item?.fraisGestionSaisi ?? item?.fraisGestionAutocomplete),
      ],
      retrocessionAssureurSaisi: [
        toPercent(
          item?.retrocessionAssureurSaisi ??
            item?.retrocessionAssureurAutocomplete,
        ),
      ],
    };
  }

  extract() {
    const formValue =
      this.form.value?.products?.[0]?.parameters?.supportRepartition ?? [];

    const supportRepartition = _.filter(
      formValue,
      o => o.isin != undefined && o.isin != null,
    ).map(o => ({
      isin: o.isin,
      name: o.name,
      type: o.type,
      riskScore: o?.riskScore ?? null,
      initalRepartition: o?.initalRepartition ?? 0,
      monthlyRepartition: o?.monthlyRepartition ?? 0,
      upfrontAssureurAutocomplete: o.upfrontAssureurAutocomplete ?? null,
      upfrontEmetteurAutocomplete: o.upfrontEmetteurAutocomplete ?? null,
      fraisGestionAutocomplete: o.fraisGestionAutocomplete ?? null,
      retrocessionAssureurAutocomplete:
        o.retrocessionAssureurAutocomplete ?? null,
      // Absent de `o` (donc undefined, converti en null par fromPercent) si le champ était
      // désactivé (organisation EUODIA) : ne persiste alors jamais de surcharge, la résolution
      // retombe sur la base support.
      upfrontAssureurSaisi: fromPercent(o.upfrontAssureurSaisi),
      upfrontEmetteurSaisi: fromPercent(o.upfrontEmetteurSaisi),
      fraisGestionSaisi: fromPercent(o.fraisGestionSaisi),
      retrocessionAssureurSaisi: fromPercent(o.retrocessionAssureurSaisi),
    }));

    const products = this.form.value?.products;
    return _.map(products, o => ({
      ...o,
      parameters: {
        ...o.parameters,
        supportRepartition,
      },
    }));
  }
}
